import type {Href} from "expo-router";

// Where a notification points. Pure on purpose: `data` in, a route out, nothing else — no
// navigation, no hooks, no platform. That is what makes every branch of the payload contract
// (unknown route, missing ids, a `data` that is not even an object) something you can read off
// this file and test directly, rather than something you can only find out by tapping a push on
// a device.
//
// This is also the ONLY module that knows what the producer puts in `data`. The foreground
// handler needs the same payload's channel id, so it asks here rather than re-parsing.
//
// The payload the backend's chat producer sends is
// `{route: "chat", channelId: "<emb.chat channel id>", orgId: "<org id>"}`. `orgId` is present for
// DMs as well as group channels — decision D1 made every chat channel org-scoped, so there is no
// such thing as an org-less chat notification. A payload missing it is malformed, NOT a DM, and it
// is deliberately not repaired by substituting one of the user's organizations (ruled out by
// D1/D2): a deep link into the wrong org is worse than no deep link.

/** The key under which the producer names the destination, and the one destination we know. */
const ROUTE_KEY = "route";
const CHAT_ROUTE = "chat";

/**
 * Where a notification goes when its route cannot be resolved: an unknown `route` from a newer
 * backend, a payload that lost a field, a `data` that is not an object at all.
 *
 * The inbox can serve as the catch-all precisely because it is NOT org-scoped — there is no org
 * id to supply and none to guess — and because the notification the user tapped is in it, so they
 * still reach what they were told about, one tap further away.
 *
 * `as unknown as Href`: expo-router's generated route union in `.expo/types` predates this screen
 * and only refreshes on the next `expo start`. Same cast and same reason as
 * `app/(app)/organization/[orgId]/chat/index.tsx`.
 */
export const NOTIFICATION_INBOX_ROUTE = "/notifications" as unknown as Href;

/**
 * `data` as something whose keys can be read, or null for everything else.
 *
 * The payload arrives from the OS on one path and from REST on another, and neither is validated
 * upstream, so `null`, a string, a number and an array all have to land somewhere defined. Arrays
 * are excluded explicitly rather than left to fall through on a missing `route`, so the reason
 * they are not payloads is stated once.
 */
function asPayload(data: unknown): Record<string, unknown> | null {
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        return null;
    }
    return data as Record<string, unknown>;
}

/** A key read as a non-empty string. An empty string counts as absent — it routes nowhere. */
function readString(payload: Record<string, unknown>, key: string): string | null {
    const value = payload[key];
    return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * The chat channel a notification is about, or null when it is not about one.
 *
 * Split out of {@link resolveNotificationRoute} because the foreground handler wants the SUBJECT,
 * not a destination: it has to decide whether the channel this push concerns is the one already on
 * screen, and turning that into an `Href` first only to take it apart again would put a second
 * reading of the payload contract in a second file.
 *
 * Note this answers for a payload with a `channelId` but no `orgId`, where `resolveNotificationRoute`
 * gives up. Suppressing a banner for a channel the user is demonstrably looking at is safe even
 * when the payload is too incomplete to navigate with.
 */
export function notificationChannelId(data: unknown): string | null {
    const payload = asPayload(data);
    if (!payload || readString(payload, ROUTE_KEY) !== CHAT_ROUTE) {
        return null;
    }
    return readString(payload, "channelId");
}

/**
 * The route a tap on this notification should open, or null when the payload names none.
 *
 * Null rather than a built-in fallback, because the two callers want different things from an
 * unroutable notification: the push-response listener still has to send the user somewhere and
 * applies {@link NOTIFICATION_INBOX_ROUTE}, while a tap on an inbox row is already there and
 * should simply do nothing. Baking the fallback in here would make the second case navigate to
 * itself.
 */
export function resolveNotificationRoute(data: unknown): Href | null {
    const payload = asPayload(data);
    if (!payload) {
        return null;
    }

    switch (readString(payload, ROUTE_KEY)) {
        case CHAT_ROUTE: {
            const channelId = readString(payload, "channelId");
            const orgId = readString(payload, "orgId");
            if (!channelId || !orgId) {
                return null;
            }
            return {
                pathname: "/organization/[orgId]/chat/[channelId]",
                params: {orgId, channelId}
            } as unknown as Href;
        }
        default:
            // An unknown route and an absent one are the same answer on purpose: a build that
            // predates a new notification type must fall back, not crash.
            return null;
    }
}
